/*
 * Copyright © 2022 EPO: European Patent Office.
 * ALL RIGHTS RESERVED
 */

import { Trans, useTranslation } from 'react-i18next';
import React, { useMemo, useState } from 'react';
import { callApi } from 'api/helpers/api.helpers';
import { SubmissionStatus } from '../../models/SubmissionStatus';
import draftApi from '../../api/draft.api';

export default function useSubmissionStatusChangeDialog(submission, handleConfirm, handleCancel, revertStatusAction) {
  const [comments, setComments] = useState('');
  const { t } = useTranslation();
  const readyToSendStatus =
    submission?.status === SubmissionStatus.READY_TO_PAY.id
      ? t('submission.status.ready.to.pay')
      : t('submission.status.ready.to.send');

  const messages = useMemo(() => {
    switch (revertStatusAction) {
      case SubmissionStatus.DRAFT.id: {
        const fromStatus =
          submission.status === SubmissionStatus.READY_TO_SIGN.id
            ? t('submission.status.ready.to.sign')
            : readyToSendStatus;
        return {
          sendBackTo: t('actions.send.back.draft'),
          warning: (
            <Trans
              i18nKey="messages.submission.status.change"
              values={{
                fromStatus,
                toStatus: t('submission.status.draft'),
              }}
            />
          ),
          comment: t('messages.submission.back.draft.why'),
        };
      }
      case SubmissionStatus.READY_TO_SIGN.id:
        return {
          sendBackTo: t('actions.send.back.sign'),
          warning: (
            <Trans
              i18nKey="messages.submission.status.change"
              values={{
                fromStatus: readyToSendStatus,
                toStatus: t('submission.status.ready.to.sign'),
              }}
            />
          ),
          comment: t('messages.submission.ready-sign.why'),
        };
      default:
        return {};
    }
  }, [revertStatusAction, submission]);

  const handleChange = event => setComments(event.target.value);

  const storeSubmissionStatusChange = async () => {
    const submissionStatusChangeModel = {
      previousStatus: submission.status,
      currentStatus: revertStatusAction,
      comments,
    };
    await callApi(draftApi.rollbackStatus)(submission.id, submissionStatusChangeModel);
    setComments('');
    handleConfirm();
  };

  const cancelDialog = async () => {
    setComments('');
    handleCancel();
  };

  return {
    comments,
    handleChange,
    storeSubmissionStatusChange,
    cancelDialog,
    messages,
  };
}
